import React, { useState, Suspense, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Paper from '@mui/material/Paper';
import TableBody from '@mui/material/TableBody';
import { useBusinessRules, updateBusinessRule, assignBusinessRules, BusinessActions, BusinessAction, useBusinessRuleSets, updateBusinessRuleSet, copyBusinessRuleSet, setBusinessRuleSetId, useBusinessRuleSetId, downloadBusinessRuleSet } from '../../Data/BusinessRulesData';
import { createStyles, Theme, Button, Grid, TextField } from '@mui/material';
import { BusinessRule, BusinessRuleSet } from '../../utils/BusinessRulesEngine';
import { SET_LOADING, SET_SERVER_MESSAGE, ApplicationDispatch } from '../../store/application/types';
import { useDispatch, useSelector } from 'react-redux';
import { ActionEditor } from './ActionEditor';
import { isValidDate, reorder } from '../../utils/utils';
import IconButton from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import LinearProgress from '@mui/material/LinearProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import GetAppIcon from '@mui/icons-material/GetApp';
import { ILoggedInEmployee } from '../../Interfaces/Admin/ILoggedInEmployee';
import { AppState } from '../../store/rootReducer';
import {DatePickerChangeEvent} from '@progress/kendo-react-dateinputs';
import {DatePickerWithYearSanitizer} from '@amsanalytics/react-shared-library';

const BusinessRuleEditor = React.lazy(() => import('./BusinessRuleEditor'));



export interface BusinessRulesProps {

}

export const BusinessRules: React.FC<BusinessRulesProps> = props => {
  const dispatch = useDispatch<ApplicationDispatch>();
  const { data: businessRules, isFetching } = useBusinessRules();
  const [editingRule, setEditingRule] = useState<BusinessRule | undefined>(undefined);
  const [editingAction, setEditingAction] = useState<string | undefined>(undefined);
  const activeUser: ILoggedInEmployee | undefined = useSelector((state: AppState) => state.applications.loggedInUser?.employee);

  const [sortedBusinessRules, setSortedBusinessRules] = useState<BusinessRule[]>([]);
  useEffect(() => {
    if (businessRules) {
      setSortedBusinessRules(businessRules);
    }
  }, [businessRules]);

  const { data: businessRuleSets } = useBusinessRuleSets(activeUser);
  const [editingBusinessRuleSet, setEditingBusinessRuleSet] = useState<BusinessRuleSet>();
  const [businessRuleSetMenuItem, setBusinessRuleSetMenuItem] = useState<BusinessRuleSet>();
  const [businessRuleSetMenuAnchor, setBusinessRuleSetMenuAnchor] = useState<Element | undefined>();
  const { data: businessRuleSetId } = useBusinessRuleSetId();

  const [editHistoricalRuleSets, setEditHistoricalRuleSets] = useState(false);

  const currentBusinessRuleSet = businessRuleSets?.find(x => x.id === businessRuleSetId);
  const canEditSelected = (businessRuleSet?: BusinessRuleSet) => {
    const canEdit = (businessRuleSet ?? false) && (
      editHistoricalRuleSets ||
      !businessRuleSet?.startDate ||
      (new Date(businessRuleSet?.startDate) > new Date())
    );
    return canEdit;
  }
  const canEdit = canEditSelected(currentBusinessRuleSet);

  if (editingAction) {
    return (
      <ActionEditor action={editingAction}
        businessRules={businessRules}
        onSave={async (ids) => {
          try {
            dispatch({ type: SET_LOADING, payload: true });
            await assignBusinessRules(editingAction, ids, currentBusinessRuleSet!.id);
            setEditingAction(undefined);
          } catch (error: any) {
            dispatch({
              type: SET_SERVER_MESSAGE,
              payload: { isError: true, message: error as string }
            });
          } finally {
            dispatch({ type: SET_LOADING, payload: false });
          }
        }}
        onCancel={() => {
          setEditingAction(undefined);
        }}
        canEdit={canEdit}
      />
    )
  } else if (editingRule) {
    return (
      <Suspense fallback={<div style={{ padding: 24 }}>Loading...</div>}>
        <BusinessRuleEditor businessRule={editingRule}
          onSave={async (businessRule: BusinessRule) => {
            try {
              dispatch({ type: SET_LOADING, payload: true });
              await updateBusinessRule(businessRule);
              // setEditingRule(undefined);
            } catch (error) {
              dispatch({
                type: SET_SERVER_MESSAGE,
                payload: { isError: true, message: error as string }
              });
            } finally {
              dispatch({ type: SET_LOADING, payload: false });
            }
          }}
          onCancel={() => {
            setEditingRule(undefined);
          }}
          canEdit={canEdit}
        />
      </Suspense>
    )
  } else {
    return (
      <>
        <Menu
          anchorEl={businessRuleSetMenuAnchor}
          open={!!businessRuleSetMenuAnchor}
          onClose={() => {
            setBusinessRuleSetMenuItem(undefined);
            setBusinessRuleSetMenuAnchor(undefined);
          }}
        >
          <MenuItem disabled={(!businessRuleSetMenuItem?.id) || !canEditSelected(businessRuleSetMenuItem)}
            onClick={() => {
              if (businessRuleSetMenuItem) {
                setEditingBusinessRuleSet({ ...businessRuleSetMenuItem });
                setBusinessRuleSetMenuItem(undefined);
                setBusinessRuleSetMenuAnchor(undefined);
              }
            }}
          >
            <EditIcon />
            Edit
          </MenuItem>
          <MenuItem
            onClick={async () => {
              const id = businessRuleSetMenuItem?.id;
              setBusinessRuleSetMenuItem(undefined);
              setBusinessRuleSetMenuAnchor(undefined);
              try {
                dispatch({ type: SET_LOADING, payload: true });
                await copyBusinessRuleSet(id);
              } finally {
                dispatch({ type: SET_LOADING, payload: false });
              }
            }}
          >
            <FileCopyIcon />
            Copy
          </MenuItem>
          <MenuItem
            onClick={async () => {
              setBusinessRuleSetMenuItem(undefined);
              setBusinessRuleSetMenuAnchor(undefined);
              try {
                dispatch({ type: SET_LOADING, payload: true });
                const fileName = `LosBusinessRules_${window.location.hostname}.js.txt`;
                await downloadBusinessRuleSet(businessRuleSetMenuItem?.id ?? 0, fileName, dispatch);
              } finally {
                dispatch({ type: SET_LOADING, payload: false });
              }
            }}
          >
            <GetAppIcon />
            Download Rules
          </MenuItem>
        </Menu>
        <Grid container spacing={3} style={{ padding: 24, width: '100%' }}>
          <Grid item xs={12} md={4}>
            <Typography variant="h5">Rule Sets</Typography>
            <TableContainer component={Paper} style={{maxHeight: '70vh'}}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {businessRuleSets?.map((x, i) =>
                    <TableRow key={i}
                      style={{
                        backgroundColor: x.id === businessRuleSetId ? '#eee' : '',
                        cursor: 'pointer',
                      }}
                      hover
                      onClick={(e) => {
                        if (!editingBusinessRuleSet
                          && !businessRuleSetMenuAnchor
                        ) {
                          setBusinessRuleSetId(x.id);
                        }
                      }}
                    >
                      <TableCell>
                        {editingBusinessRuleSet?.id == x?.id
                          ?
                          <TextField size="small" margin="none"
                            fullWidth
                            multiline
                            variant="outlined"
                            value={editingBusinessRuleSet?.description || ''}
                            onChange={(e) => {
                              setEditingBusinessRuleSet({
                                ...editingBusinessRuleSet,
                                description: e.target.value,
                              });
                            }}
                          />
                          : x?.description
                        }
                      </TableCell>
                      <TableCell>
                        {editingBusinessRuleSet?.id == x?.id
                          ?
                          <DatePickerWithYearSanitizer
                            value={isValidDate(editingBusinessRuleSet?.startDate) ? new Date(editingBusinessRuleSet.startDate!) : null}
                            onChange={(event: DatePickerChangeEvent) => {
                              setEditingBusinessRuleSet({
                                ...editingBusinessRuleSet,
                                startDate: event.value?.toString(),
                              });
                            }}
                          />
                          : isValidDate(x.startDate)
                            ? new Date(x.startDate!).toLocaleString()
                            : ''
                        }
                      </TableCell>
                      <TableCell>
                        {editingBusinessRuleSet?.id == x?.id
                          ?
                          <div style={{ width: 54 }}>
                            <IconButton edge="end"
                              size="small"
                              onClick={async (e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                try {
                                  dispatch({ type: SET_LOADING, payload: true });
                                  await updateBusinessRuleSet(editingBusinessRuleSet);
                                } finally {
                                  setEditingBusinessRuleSet(undefined);
                                  dispatch({ type: SET_LOADING, payload: false });
                                }
                              }}
                            >
                              <SaveIcon />
                            </IconButton>
                            <IconButton edge="end"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setEditingBusinessRuleSet(undefined);
                              }}
                            >
                              <CancelIcon />
                            </IconButton>
                          </div>
                          :
                          <IconButton edge="end"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              setBusinessRuleSetMenuItem(x);
                              setBusinessRuleSetMenuAnchor(e.target as Element);
                            }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        }
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <div style={{ margin: 10 }}>
              <FormControlLabel
                control={
                  <Switch checked={editHistoricalRuleSets} onChange={() => {
                    setEditHistoricalRuleSets(!editHistoricalRuleSets);
                  }}
                    size="small"
                    name="Edit historical rulesets" />
                }
                label="Edit historical rulesets"
              />
              <Tooltip title="Use this setting sparingly to fix mistakes.">
                <InfoIcon fontSize="small" color="action"
                  style={{
                    marginLeft: 5,
                    marginBottom: -5,
                  }}
                />
              </Tooltip>
            </div>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="h5">Rules</Typography>
            {isFetching && <LinearProgress />}
            <TableContainer component={Paper} style={{maxHeight: '70vh'}}>
              {/* <DragDropContext onDragEnd={onDragEnd}> */}
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Description</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Active</TableCell>
                      {/* <TableCell>Created Date</TableCell>
                  <TableCell>Modified Date</TableCell> */}
                    </TableRow>
                  </TableHead>
                  {/* <Droppable droppableId="BusinessRules">
                    {(provided, snapshot) => ( */}
                      <TableBody
                        // {...provided.droppableProps}
                        // ref={provided.innerRef}
                      >
                        {((sortedBusinessRules?.length ?? 0) === 0) && !isFetching &&
                          <TableRow>
                            <TableCell>
                              {canEdit
                                ? "No Business Rules Yet!"
                                : "Select a rule set"
                              }
                            </TableCell>
                          </TableRow>
                        }
                        {(sortedBusinessRules.length > 0) &&
                          sortedBusinessRules.map((br, i) =>
                                <TableRow
                                  hover
                                  style={{cursor: 'pointer'}}
                                  onClick={(e) => {
                                    setEditingRule(br);
                                  }}
                                >
                                  <TableCell>
                                    {br.description}
                                  </TableCell>
                                  <TableCell>
                                    {br.type}
                                  </TableCell>
                                  <TableCell>
                                    {!br.deleted ? "Yes" : "No"}
                                  </TableCell>
                                  {/* <TableCell>
                              {br.createdDate && (new Date(br.createdDate)).toString()}
                            </TableCell>
                            <TableCell>
                              {br.modifiedDate && (new Date(br.modifiedDate)).toString()}
                            </TableCell> */}
                                </TableRow>
                          )
                        }
                      </TableBody>
                </Table>
            </TableContainer>

            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                setEditingRule({
                  id: 0,
                  businessRuleSetId: businessRuleSetId,
                });
              }}
              style={{ margin: 10 }}
              disabled={!canEdit}
            >
              Add
            </Button>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="h5">Actions</Typography>
            <TableContainer component={Paper} style={{maxHeight: '70vh'}}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell># Rules</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.keys(BusinessActions)
                    .map((x, i, a) =>
                      <TableRow hover style={{cursor: 'pointer'}}
                        key={x}
                        onClick={() => {
                          setEditingAction(x);
                        }}
                      >
                        <TableCell>{x}</TableCell>
                        <TableCell>
                          {(BusinessActions[a[i - 1] as BusinessAction]?.type
                            !== BusinessActions[a[i] as BusinessAction]?.type)
                            && BusinessActions[x as BusinessAction].type}
                        </TableCell>
                        <TableCell>
                          {businessRules?.filter(br => br.businessRuleActions?.some(a => !a.deleted && a.action === x))
                            ?.length || ''}
                        </TableCell>
                      </TableRow>
                    )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </>
    );
  }
}
